import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { CalendarBlankIcon, CaretLeftIcon, CaretRightIcon } from "@phosphor-icons/react";
import {
  NO_WEEK, buildWeekOptions, canReportWeek, coveredMonday, mondayOf, toIsoDay, weekLabel, weekRange,
} from "@/lib/reportWeeks";

const DAY = 86400000;
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const HEADS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/*
  Calendar for choosing which week a report covers. Picking a day reports the
  Monday-to-Saturday week before the next Monday on or after it (see
  coveredMonday in lib/reportWeeks.js): a Monday gives the week just ended, any
  other day gives the week it sits in. The covered week is shaded across its
  row, and a line under the field spells out the dates, because "pick a Monday,
  get the week before" is not obvious on its own.

  Hand-built rather than the shadcn Calendar: that wrapper targets an older
  react-day-picker than the one installed, so its class names don't apply.
*/
export default function WeekPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const selectedMonday = value && value !== NO_WEEK ? new Date(`${value}T00:00:00Z`) : null;
  const [view, setView] = useState(() => {
    const base = selectedMonday || new Date();
    return new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth(), 1));
  });

  const now = new Date();
  // Last week and 2 weeks ago. Index 0 is "Overall", 1 is the current week — which
  // has not ended, so it is never offered — and a week must also pass canReportWeek.
  const presets = buildWeekOptions(3, now)
    .slice(2)
    .filter((o) => canReportWeek(new Date(`${o.value}T00:00:00Z`), now));

  // Six rows of seven, starting on the Monday on/before the 1st.
  const gridStart = mondayOf(view);
  const cells = Array.from({ length: 42 }, (_, i) => new Date(gridStart.getTime() + i * DAY));

  const inSelectedWeek = (d) =>
    selectedMonday && d >= selectedMonday && d <= new Date(selectedMonday.getTime() + 5 * DAY);

  const pick = (d) => {
    const monday = coveredMonday(d);
    if (!canReportWeek(monday, now)) return;
    onChange(toIsoDay(monday));
    setOpen(false);
  };

  const shiftMonth = (n) => setView(new Date(Date.UTC(view.getUTCFullYear(), view.getUTCMonth() + n, 1)));
  const trigger = selectedMonday ? weekLabel(selectedMonday, now) : "Overall — current status";

  return (
    <div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="w-full justify-start rounded-sm font-normal"
            data-testid="report-week-picker"
          >
            <CalendarBlankIcon size={14} className="mr-2 shrink-0" />
            <span className="truncate">{trigger}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[300px] p-3 rounded-sm">
          <div className="flex flex-wrap gap-1.5 mb-3">
            <button
              type="button"
              onClick={() => { onChange(NO_WEEK); setOpen(false); }}
              className={`text-[11px] font-mono border rounded-sm px-2 py-1 ${value === NO_WEEK ? "bg-[#0176D3] text-white border-[#0176D3]" : "hover:bg-zinc-50"}`}
              data-testid="week-preset-overall"
            >
              Overall
            </button>
            {presets.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => { onChange(o.value); setOpen(false); }}
                className={`text-[11px] font-mono border rounded-sm px-2 py-1 ${value === o.value ? "bg-[#0176D3] text-white border-[#0176D3]" : "hover:bg-zinc-50"}`}
                data-testid={`week-preset-${o.value}`}
              >
                {o.label.split(" · ")[0]}
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between mb-2">
            <button type="button" onClick={() => shiftMonth(-1)} className="p-1 rounded-sm hover:bg-zinc-100" aria-label="Previous month">
              <CaretLeftIcon size={14} />
            </button>
            <div className="text-sm font-medium">{MONTH_NAMES[view.getUTCMonth()]} {view.getUTCFullYear()}</div>
            <button type="button" onClick={() => shiftMonth(1)} className="p-1 rounded-sm hover:bg-zinc-100" aria-label="Next month">
              <CaretRightIcon size={14} />
            </button>
          </div>

          <div className="grid grid-cols-7 text-center text-[10px] font-mono uppercase text-zinc-500 mb-1">
            {HEADS.map((h) => <div key={h}>{h}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-y-0.5">
            {cells.map((d) => {
              const reportable = canReportWeek(coveredMonday(d), now);
              const outside = d.getUTCMonth() !== view.getUTCMonth();
              const shaded = inSelectedWeek(d);
              const sunday = d.getUTCDay() === 0;
              return (
                <button
                  key={d.toISOString()}
                  type="button"
                  disabled={!reportable}
                  onClick={() => pick(d)}
                  title={reportable ? `Report: ${weekRange(coveredMonday(d))}` : "That week has not ended yet"}
                  className={[
                    "h-8 text-xs",
                    shaded ? "bg-[#DBEAFE] text-[#0F172A]" : "",
                    reportable ? "hover:bg-zinc-100" : "text-zinc-300 cursor-not-allowed",
                    outside && !shaded ? "text-zinc-400" : "",
                    sunday && !shaded ? "text-zinc-400" : "",
                  ].join(" ")}
                >
                  {d.getUTCDate()}
                </button>
              );
            })}
          </div>
          <p className="text-[10px] text-zinc-500 mt-2 leading-snug">
            Pick a Monday for the week that just ended, or any day to report the week it falls in.
            Weeks run Monday to Saturday.
          </p>
        </PopoverContent>
      </Popover>

      {/* Spell out exactly what will be covered. */}
      <div className="text-[11px] font-mono text-zinc-600 mt-1.5" data-testid="report-week-covers">
        {selectedMonday ? `Covers ${weekRange(selectedMonday)}` : "Covers everything, as it stands now"}
      </div>
    </div>
  );
}
